import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminUser } from "@/queries/adminUsers";

import { ActiveBadge } from "./ActiveBadge";
import { RelativeTime } from "./RelativeTime";
import { RoleBadge } from "./RoleBadge";
import { UserRowActions } from "./UserRowActions";

interface AdminUsersTableProps {
  users: AdminUser[];
  selfId: string;
  onResetPassword: (user: AdminUser) => void;
  onDeactivate: (user: AdminUser) => void;
  onDelete: (user: AdminUser) => void;
}

export function AdminUsersTable({
  users,
  selfId,
  onResetPassword,
  onDeactivate,
  onDelete,
}: AdminUsersTableProps) {
  const { t } = useTranslation("admin-users");
  const navigate = useNavigate();

  const goToDetail = (id: string) => {
    void navigate({ to: "/admin/users/$id", params: { id } });
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("list.columns.email")}</TableHead>
            <TableHead>{t("list.columns.displayName")}</TableHead>
            <TableHead>{t("list.columns.role")}</TableHead>
            <TableHead>{t("list.columns.active")}</TableHead>
            <TableHead>{t("list.columns.lastLogin")}</TableHead>
            <TableHead className="w-12 text-right">
              <span className="sr-only">{t("list.columns.actions")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const isSelf = user.id === selfId;
            return (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  <button
                    type="button"
                    onClick={() => goToDetail(user.id)}
                    className="text-left hover:underline focus-visible:underline focus-visible:outline-none"
                  >
                    {user.email}
                  </button>
                  {isSelf ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t("row.youSelf")}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {user.display_name ?? "—"}
                </TableCell>
                <TableCell>
                  <RoleBadge role={user.role} />
                </TableCell>
                <TableCell>
                  <ActiveBadge isActive={user.is_active} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <RelativeTime iso={user.last_login_at} />
                </TableCell>
                <TableCell className="text-right">
                  <UserRowActions
                    user={user}
                    isSelf={isSelf}
                    onViewDetails={() => goToDetail(user.id)}
                    onResetPassword={() => onResetPassword(user)}
                    onDeactivate={() => onDeactivate(user)}
                    onDelete={() => onDelete(user)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
